import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Building2, Check, Crown, Loader2, Sparkles, Zap,
} from 'lucide-react'
import { getCatalog, formatCredits, type Catalog, type CatalogPlan } from '@/api/billing'
import { changePlan } from '@/api/payments'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import { refreshCredits } from '@/store/creditsStore'

/**
 * Plan comparison, driven entirely by the server catalog.
 *
 * Nothing about a plan is hardcoded here any more — names, prices, included
 * credits and limits all come from what the instance admin configured, which is
 * why "SaaS/Enterprise" now reads the same on every surface.
 *
 * This IS a checkout surface, so localized prices are shown; credits-only
 * applies everywhere else.
 */

const PLAN_ICON: Record<string, typeof Zap> = { F: Zap, P: Crown, S: Building2 }
const PLAN_COLOR: Record<string, string> = { F: '#4caf50', P: '#ffa726', S: '#42a5f5' }

function limitLabel(value: number | null, noun: string): string {
  if (value === null) return `Unlimited ${noun}`
  return `${value.toLocaleString()} ${noun}${value === 1 ? '' : ''}`
}

/** The feature bullets for a plan, derived from its configured values. */
function planFeatures(plan: CatalogPlan): string[] {
  const out: string[] = []
  if (plan.included_monthly_credits > 0) {
    out.push(`${formatCredits(plan.included_monthly_credits)} credits every month`)
  } else if (plan.trial_credits > 0) {
    out.push(`${formatCredits(plan.trial_credits)} trial credits to start`)
  }
  out.push('Bring your own API key (never charged)')
  out.push(limitLabel(plan.max_applications, 'applications'))
  const credit = plan.max_credit_turns_per_day
  const own = plan.max_own_ai_turns_per_day
  if (credit !== undefined && own !== undefined) {
    if (credit === null && own === null) {
      out.push('Unlimited AI agent turns')
    } else {
      out.push(credit === null
        ? 'Unlimited AI agent turns on credits'
        : `${credit.toLocaleString()} AI agent turns a day on credits`)
      out.push(own === null
        ? 'Unlimited turns on your own key or a free model'
        : `${own.toLocaleString()} turns a day on your own key or a free model`)
    }
  }
  if (plan.max_tables_per_project !== null) {
    out.push(`Up to ${plan.max_tables_per_project.toLocaleString()} tables per application`)
  }
  if (plan.max_screens_per_project !== null) {
    out.push(`Up to ${plan.max_screens_per_project.toLocaleString()} screens per application`)
  }
  if (plan.max_records_per_project !== null) {
    out.push(`Up to ${plan.max_records_per_project.toLocaleString()} records per application`)
  }
  if (plan.allow_runtime_download) out.push('Download a standalone runtime package')
  return out
}

export default function PlanSelectorPage() {
  const navigate = useNavigate()
  const { checkout, busy } = useRazorpayCheckout()
  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly')
  const [changing, setChanging] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setCatalog(await getCatalog())
    } catch {
      toast.error('Could not load the plan catalog')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Only offer the yearly toggle when a plan actually has a yearly price —
  // a toggle that reveals nothing but "contact us" is worse than no toggle.
  const hasYearly = useMemo(
    () => (catalog?.plans ?? []).some((p) => !!p.prices.yearly),
    [catalog],
  )

  const selectPlan = useCallback(async (plan: CatalogPlan) => {
    if (plan.is_current && !catalog?.subscription.cancel_at_period_end) {
      toast('You are already on this plan')
      return
    }
    setChanging(plan.code)
    try {
      // Downgrading to a free plan takes no payment — the legacy endpoint
      // handles cancellation + the mirror in one call.
      if (!plan.prices[period]) {
        const res = await changePlan(plan.code)
        toast.success(res?.message || 'Plan updated')
        refreshCredits()
        await load()
        return
      }
      const result = await checkout({
        purpose: 'plan_subscription',
        target_ref: plan.code,
        billing_period: period,
        description: `Twasta.ai ${plan.display_name}`,
      })
      if (result.cancelled) return
      if (!result.ok) {
        toast.error(result.error || 'The payment did not complete')
        return
      }
      toast.success(`You're on ${plan.display_name}`)
      refreshCredits()
      await load()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Could not change the plan')
    } finally {
      setChanging(null)
    }
  }, [catalog, checkout, load, period])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Loader2 className="spin" size={28} />
      </div>
    )
  }

  const plans = catalog?.plans ?? []

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
      <button
        className="btn btn-secondary"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/builder'))}
        style={{ marginBottom: '1rem', gap: '0.375rem' }}
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.8125rem', margin: '0 0 0.375rem' }}>Choose your plan</h1>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', margin: 0 }}>
          Every plan includes credits for AI on the platform key. Using your own
          API key is always free.
        </p>

        {hasYearly && (
          <div style={{
            display: 'inline-flex', gap: '0.25rem', marginTop: '1rem', padding: '0.25rem',
            background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
            borderRadius: 999,
          }}>
            {(['monthly', 'yearly'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: '0.3125rem 0.875rem', borderRadius: 999, border: 'none',
                  fontSize: '0.875rem', cursor: 'pointer',
                  background: period === p ? 'var(--color-accent)' : 'transparent',
                  color: period === p ? '#fff' : 'var(--color-text-secondary)',
                }}
              >
                {p === 'monthly' ? 'Monthly' : 'Yearly'}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{
        display: 'grid', gap: '1rem',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      }}>
        {plans.map((plan) => {
          const Icon = PLAN_ICON[plan.code] || Sparkles
          const color = PLAN_COLOR[plan.code] || 'var(--color-accent)'
          const price = plan.prices[period]
          const highlighted = plan.code === 'P'
          return (
            <div
              key={plan.code}
              className="card"
              style={{
                padding: '1.5rem',
                border: highlighted ? `2px solid ${color}` : '1px solid var(--color-border)',
                position: 'relative',
                display: 'flex', flexDirection: 'column',
              }}
            >
              {plan.is_current && (
                <span style={{
                  position: 'absolute', top: '0.75rem', right: '0.75rem',
                  fontSize: '0.8125rem', padding: '0.125rem 0.5rem', borderRadius: 999,
                  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                  color: 'var(--color-text-secondary)',
                }}>
                  Current plan
                </span>
              )}

              <Icon size={26} style={{ color }} />
              <h2 style={{ fontSize: '1.3125rem', margin: '0.625rem 0 0.25rem' }}>{plan.display_name}</h2>
              {plan.description && (
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '0 0 0.75rem' }}>
                  {plan.description}
                </p>
              )}

              <div style={{ margin: '0.5rem 0 1rem' }}>
                <span style={{ fontSize: '2.0625rem', fontWeight: 700 }}>
                  {price ? price.display : 'Free'}
                </span>
                {price && (
                  <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem' }}>
                    {period === 'monthly' ? ' /month' : ' /year'}
                  </span>
                )}
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.25rem', flex: 1 }}>
                {planFeatures(plan).map((f) => (
                  <li key={f} style={{
                    display: 'flex', gap: '0.5rem', alignItems: 'flex-start',
                    fontSize: '0.875rem', marginBottom: '0.4375rem',
                  }}>
                    <Check size={14} style={{ color, flexShrink: 0, marginTop: 2 }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                className={highlighted ? 'btn btn-primary' : 'btn btn-secondary'}
                onClick={() => selectPlan(plan)}
                disabled={busy || changing !== null || (plan.is_current && !catalog?.subscription.cancel_at_period_end)}
                style={{ width: '100%' }}
              >
                {changing === plan.code ? (
                  <Loader2 size={15} className="spin" />
                ) : plan.is_current ? (
                  'Your plan'
                ) : price ? (
                  `Choose ${plan.display_name}`
                ) : (
                  `Switch to ${plan.display_name}`
                )}
              </button>

              {/* A paid plan with no gateway plan id cannot start a recurring
                  subscription — say so here rather than failing at checkout. */}
              {price && !price.has_provider_plan && period === 'monthly' && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                  Billed as a one-time payment for this period.
                </div>
              )}
            </div>
          )
        })}
      </div>

      {!plans.length && (
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            No plans are published yet. Contact your administrator.
          </p>
        </div>
      )}
    </div>
  )
}
